import {css, StyleSheet} from 'aphrodite';
import React, {useCallback, useEffect, useMemo, useState} from 'react';

import {QueryResultType, useSkippableBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {accessibleObjs} from 'graphql-client/InaccessibleInfo';
import {
  ConnectionSearchInputQuery,
  ConnectionSearchInputQueryVariables,
} from 'instruments/connections/__generated/ConnectionSearchInputQuery';
import {getTransformDataTypes} from 'instruments/connections/ConnectionDAO';
import {
  CONNECTION_SEARCH_INPUT_QUERY,
  QueryingConnectionSearchInput,
} from 'instruments/connections/ConnectionSearchInput';
import {
  InstrumentData,
  InstrumentDataFileType,
  InstrumentFileStatus,
} from 'instruments/instrument-data/InstrumentDataConstants';
import InstrumentDataTable from 'instruments/instrument-data/InstrumentDataTable';
import {setValueInConfig} from 'pipelines/generalized-step-config-editor/step-config-settings/StepConfigSettingsUtils';
import {CustomComponentEditorProps} from 'pipelines/generalized-step-config-editor/StepConfigEditorTypes';
import {FilterKey} from 'search/SearchConstants';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import Menu from 'ui-library/menus/Menu';
import Option from 'ui-library/menus/Option';
import {capitalize, constantCaseToWords} from 'util/NameUtil';

import {ConnectionListingGraphQL} from '../../../../../graphql-fragments/instruments/__generated/ConnectionListingGraphQL';

export function ConnectionSelector({
  config,
  onChangeConfig,
  disabled,
}: CustomComponentEditorProps): JSX.Element {
  const connectionId = (config.connectionId as string) ?? null;

  const handleChangeConnection = useCallback(
    (connections: Array<ConnectionListingGraphQL>) => {
      const connection = connections.length > 0 ? connections[0] : null;
      if (connection == null) {
        let updatedConfig = setValueInConfig(config, ['connectionFileId'], null);
        updatedConfig = setValueInConfig(updatedConfig, ['transformDataType'], null);
        onChangeConfig(updatedConfig, ['connectionId'], null);
        return;
      }
      if (connection.id !== connectionId) {
        let updatedConfig = setValueInConfig(config, ['connectionFileId'], null);
        updatedConfig = setValueInConfig(updatedConfig, ['transformDataType'], null);
        onChangeConfig(updatedConfig, ['connectionId'], connection.id);
      }
    },
    [config, connectionId, onChangeConfig]
  );

  return (
    <div className={css(styles.wrapper)}>
      <QueryingConnectionSearchInput
        autoFocus={false}
        onChange={handleChangeConnection}
        connectionIds={connectionId != null ? [connectionId] : []}
        shouldAllowMultiple={false}
        disabled={disabled}
      />
    </div>
  );
}

export function ConnectionFileSelector({
  config,
  onChangeConfig,
  disabled,
}: CustomComponentEditorProps): JSX.Element {
  const connectionId = (config.connectionId as string) ?? null;
  const connectionFileId = (config.connectionFileId as string) ?? null;
  const [instrumentData, setInstrumentData] = useState<Array<InstrumentData>>([]);

  const uploadedData = useMemo(
    () => instrumentData.filter((data) => data.status !== InstrumentFileStatus.NOT_UPLOADED),
    [instrumentData]
  );

  const handleSelectRow = useCallback(
    (rowIndex: number) => {
      const selectedFile = uploadedData[rowIndex];
      if (selectedFile != null) {
        const newFileId = selectedFile.id === connectionFileId ? null : selectedFile.id;
        onChangeConfig(config, ['connectionFileId'], newFileId);
      }
    },
    [config, connectionFileId, uploadedData, onChangeConfig]
  );

  const selectedRowIndices = useMemo(() => {
    const idx = connectionFileId != null ? uploadedData.findIndex(({id}) => id === connectionFileId) : -1;
    return new Set(idx >= 0 ? [idx] : []);
  }, [connectionFileId, uploadedData]);

  if (connectionId == null) {
    return (
      <InlineBanner
        bannerType={InlineBannerType.INFO}
        text="Select a connection first to browse available files."
      />
    );
  }

  return (
    <div className={css(styles.wrapper)}>
      <InstrumentDataTable
        instrumentData={uploadedData}
        allowMultiple={false}
        setInstrumentData={setInstrumentData}
        selectedRowIndices={selectedRowIndices}
        onSelectRow={handleSelectRow}
        columns={['File name', 'Status', 'Modified at']}
        filterOptions={[]}
        initialFilterValues={{[FilterKey.CONNECTION]: [connectionId]}}
        fileTypeFilter={InstrumentDataFileType.ORIGINAL}
      />
    </div>
  );
}

export function ConnectionTransformDataTypeSelector({
  config,
  onChangeConfig,
}: CustomComponentEditorProps): JSX.Element {
  const connectionId = (config.connectionId as string) ?? null;
  const transformDataType = (config.transformDataType as string) ?? null;
  const [transformDataTypes, setTransformDataTypes] = useState<{[id: string]: string} | null>(null);

  const connectionQuery = useSkippableBenchlingQuery<
    ConnectionSearchInputQuery,
    ConnectionSearchInputQueryVariables
  >(CONNECTION_SEARCH_INPUT_QUERY, {
    variables: {ids: connectionId != null ? [connectionId] : []},
    skip: connectionId == null,
  });

  const integrationAdapterId = useMemo(() => {
    if (connectionQuery.type === QueryResultType.SUCCESS) {
      const connections = accessibleObjs(connectionQuery.data.connections);
      return connections[0]?.schema.integrationAdapterId ?? null;
    }
    return null;
  }, [connectionQuery]);

  useEffect(() => {
    if (integrationAdapterId) {
      getTransformDataTypes(integrationAdapterId).then((types) => {
        setTransformDataTypes(types);
      });
    } else {
      setTransformDataTypes(null);
    }
  }, [integrationAdapterId]);

  useEffect(() => {
    if (transformDataTypes != null) {
      const keys = Object.keys(transformDataTypes);
      if (keys.length === 1 && transformDataType == null) {
        onChangeConfig(config, ['transformDataType'], keys[0]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- config excluded to prevent infinite re-renders; only auto-select when transformDataTypes loads
  }, [transformDataTypes, transformDataType, onChangeConfig]);

  if (connectionId == null) {
    return (
      <InlineBanner
        bannerType={InlineBannerType.INFO}
        text="Select a connection first to choose a data type."
      />
    );
  }

  if (integrationAdapterId == null || transformDataTypes == null) {
    return <CenteredHorizontalThrobber />;
  }

  return (
    <div className={css(styles.wrapper)}>
      <SelectDropdown
        displayName={
          transformDataType != null
            ? capitalize(constantCaseToWords(transformDataType))
            : 'Select a data type'
        }
        menu={
          <Menu
            value={transformDataType}
            height={Menu.HEIGHTS.MEDIUM}
            width={Menu.WIDTHS.FULL}
            scrollable={true}
          >
            {Object.entries(transformDataTypes).map(([value, _description]) => (
              <Option
                key={value}
                value={value}
                onSelect={() => {
                  onChangeConfig(config, ['transformDataType'], value);
                }}
              >
                {capitalize(constantCaseToWords(value))}
              </Option>
            ))}
          </Menu>
        }
      />
    </div>
  );
}

const styles = StyleSheet.create({
  wrapper: {
    marginBottom: 8,
  },
});
